import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  Typography,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { useNavigation } from "contexts/notificationsContext";
import { api, apiAntigo } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import BackdropLoading from "components/BackdropLoading";
import { ListaCopiarBaixar } from "components/ListaCopiarBaixar";

interface ModalAdicionarLiberacaoDiversaProps {
  open: boolean;
  onClose: () => void;
  liberacaoId: number | null;
  onSuccess?: () => void;
  /** Liberação já disponibilizada: abre só para consulta (plano de 14/10/2026, Frente 5). */
  somenteLeitura?: boolean;
}

interface DualListItem {
  id: number | string;
  label: string;
  subtitle?: string;
  /** Pacotes N1 (tipo 1): referência do pacote — o legado grava os pacotes agrupados por ela. */
  idReferencia?: number | null;
}

/** GET api/LiberacaoDiversaPacotes/QuemPossui (MOF novo, plano de 09/10/2026 — B1). */
interface PacoteQuemPossui {
  id: number;
  label: string;
  idReferencia: number;
  idLinha: number | null;
  /** 0 = Projetista + Linha (nível 2); 1, 2, 3 = pacote N1 (nível 1). */
  tipoPacote: number;
}

/** POST api/LiberacaoDiversaPacotes/ClientesAfetados (plano 21/10/2026, Frente 8). */
interface ClientesAfetados {
  totalSeriais: number;
  seriaisBaseAntiga: number;
  seriaisMofNovo: number;
  seriaisClientesLiberacao: number;
  clientes: { idSerial: number; serial: string; origem: string }[];
}

interface NomesClientes {
  extranetDisponivel: boolean;
  clientes: { serial: string; nomeFantasia: string; razaoSocial: string }[];
}

type ListaEditavel = "pacotes" | "tipologias" | "aplicabilidades" | "tabelas" | "clientes";

interface PacoteGravado {
  idAtualizacaoPortfolioCEM: number;
  idReferencia: number | null;
}

const ModalAdicionarLiberacaoDiversa: React.FC<ModalAdicionarLiberacaoDiversaProps> = ({
  open,
  onClose,
  liberacaoId,
  onSuccess,
  somenteLeitura = false,
}) => {
  const { openNotification } = useNavigation();
  const [isLoading, setIsLoading] = useState(false);

  // Dados principais
  const [tipoLiberacao, setTipoLiberacao] = useState<number | "">("");
  const [sistema, setSistema] = useState<string | "">("");
  const [nivelCadastro, setNivelCadastro] = useState<"nivel1" | "nivel2">("nivel1");
  
  // Referências/Linhas para N1 (Liberar cadastros nível 1)
  const [linhaIdN1, setLinhaIdN1] = useState<number | "">("");
  const [buscaN1, setBuscaN1] = useState("");
  const [pacotesDisponiveis, setPacotesDisponiveis] = useState<DualListItem[]>([]);
  const [pacotesSelecionados, setPacotesSelecionados] = useState<DualListItem[]>([]);
  
  // "Liberar para quem possui" - dropdown para todos os tipos quando nivelCadastro === "nivel1"
  const [liberarParaQuemPossui, setLiberarParaQuemPossui] = useState<number | "">("");

  // Tipologias para N2 (Liberar cadastros nível 2)
  const [projetistaId, setProjetistaId] = useState<number | "">("");
  const [linhaId, setLinhaId] = useState<number | "">("");
  const [buscaN2, setBuscaN2] = useState("");
  const [tipologiasDisponiveis, setTipologiasDisponiveis] = useState<DualListItem[]>([]);
  const [tipologiasSelecionadas, setTipologiasSelecionadas] = useState<DualListItem[]>([]);

  // Aplicabilidades
  const [buscaAplic, setBuscaAplic] = useState("");
  const [aplicabilidadesDisponiveis, setAplicabilidadesDisponiveis] = useState<DualListItem[]>([]);
  const [aplicabilidadesSelecionadas, setAplicabilidadesSelecionadas] = useState<DualListItem[]>([]);

  // Tabelas de Preços
  const [buscaTabela, setBuscaTabela] = useState("");
  const [tabelasDisponiveis, setTabelasDisponiveis] = useState<DualListItem[]>([]);
  const [tabelasSelecionadas, setTabelasSelecionadas] = useState<DualListItem[]>([]);

  // Clientes (para tipo 4 - N2)
  const [buscaCliente, setBuscaCliente] = useState("");
  const [clientesDisponiveis, setClientesDisponiveis] = useState<DualListItem[]>([]);
  const [clientesSelecionados, setClientesSelecionados] = useState<DualListItem[]>([]);

  // Opções dinâmicas (carregadas da API)
  const [tiposOpcoes, setTiposOpcoes] = useState<any[]>([]);
  const [sistemasOpcoes, setSistemasOpcoes] = useState<any[]>([]);
  const [projetistasOpcoes, setProjetistasOpcoes] = useState<any[]>([]);
  const [linhasN2Opcoes, setLinhasN2Opcoes] = useState<any[]>([]); // Para N2 (Liberar cadastros nível 2)

  // "Liberar para quem possui" (reporte de 09/10/2026): o radio Cadastro nível 1/2 é só parâmetro.
  // Nível 1 = pacotes N1 de mercado; nível 2 = Projetista + Linha liberados ao menos para um serial.
  const [opcoesQuemPossui, setOpcoesQuemPossui] = useState<PacoteQuemPossui[]>([]);
  // Plano 15/10/2026 (Frente 1): a lista vazia sem aviso parecia "não há dados" quando a consulta
  // tinha falhado — agora a tela mostra o carregamento e avisa o erro.
  const [carregandoQuemPossui, setCarregandoQuemPossui] = useState(false);
  // Cada pedido recebe um número: só o último mexe na lista e no "Carregando..." (1 → 2 → 1 rápido)
  const pedidoQuemPossuiRef = useRef(0);
  // Liberação aberta para edição (GET /LiberacaoDiversa/Liberacao): reenviada ao trocar o "quem possui"
  const liberacaoCarregadaRef = useRef<any>(null);
  // Pacotes gravados que a lista não mostra (ex.: pacotes de tipologia gravados pelo MOF antigo):
  // preservados ao regravar a referência deles
  const pacotesOcultosRef = useRef<DualListItem[]>([]);
  // Referências com pacotes gravados nesta liberação: regravadas mesmo vazias, para o legado apagar o que saiu
  const referenciasGravadasRef = useRef<Set<number>>(new Set());

  // Plano 21/10/2026 (Frente 8, P5): sem salvamento automático — o "Não" do aviso de clientes não
  // pode deixar nada gravado. As listas alteradas nesta abertura são gravadas no Salvar/Sim.
  const listasAlteradasRef = useRef<Set<ListaEditavel>>(new Set());
  const [avisoClientes, setAvisoClientes] = useState<ClientesAfetados | null>(null);
  const [confirmarDescarte, setConfirmarDescarte] = useState(false);
  // Salvar/aviso/Sim usam estado próprio: o isLoading é gatilho das cargas das listas, que
  // reescreveriam a seleção do usuário com o modal aberto (revisão crítica de 21/10).
  const [salvando, setSalvando] = useState(false);
  // Plano 22/10/2026 (Frentes 1 e 2): etapa em andamento (texto do indicador por cima dos diálogos)
  // e linhas do aviso já com os nomes dos clientes (Extranet).
  const [mensagemAndamento, setMensagemAndamento] = useState("");
  const [linhasAviso, setLinhasAviso] = useState<string[][]>([]);
  const [nomesIndisponiveis, setNomesIndisponiveis] = useState(false);
  const gravandoRef = useRef(false);
  // Liberação nova cujo cabeçalho já foi gravado numa tentativa que falhou depois: a próxima
  // tentativa reaproveita o id em vez de criar outra.
  const idCriadoRef = useRef<number | null>(null);

  // Determinar se é CEM ou PrefSuite
  // Sistemas 1-5 são CEM (Delphi) e 8 representa CEM MOBI.
  const isCEM = sistema && ["1", "2", "3", "4", "5", "8"].includes(sistema);
  const isPrefSuite = sistema && ["6", "7"].includes(sistema);
  // No tipo 4 com nível 2 o "quem possui" é o par Projetista/Linha da própria seção (layout próprio)
  const exigeQuemPossui = tipoLiberacao !== 4 || nivelCadastro === "nivel1";
  const liberacaoJaDisponivel = !!liberacaoCarregadaRef.current?.disponivel;
  // Disponibilizada = só consulta: nada se altera nem é regravado (o auto-save também para)
  const modoLeitura = somenteLeitura || liberacaoJaDisponivel;
  const modoLeituraRef = useRef(modoLeitura);
  modoLeituraRef.current = modoLeitura;

  // Carregar opções ao abrir modal
  useEffect(() => {
    if (open) {
      carregarOpcoesIniciais();
      if (liberacaoId) {
        carregarDadosLiberacao();
      } else {
        resetarFormulario();
      }
    }
  }, [open, liberacaoId]);

  // Carregar o dropdown "Liberar para quem possui" conforme o nível escolhido
  useEffect(() => {
    if (open) {
      carregarOpcoesQuemPossui(nivelCadastro);
    }
  }, [open, nivelCadastro]);

  // Limpar dados quando o tipo mudar. O nível não limpa: é só parâmetro do "quem possui"
  // (no tipo 4 a troca de nível limpa a seleção no próprio radio).
  useEffect(() => {
    if (open) {
      setPacotesSelecionados([]);
      setTipologiasSelecionadas([]);
      setAplicabilidadesSelecionadas([]);
      setTabelasSelecionadas([]);
      setClientesSelecionados([]);
      setBuscaN1("");
      setBuscaN2("");
      setBuscaAplic("");
      setBuscaTabela("");
      setBuscaCliente("");
    }
  }, [tipoLiberacao, open]);

  // Carregar projetistas e linhas ao abrir modal (para tipo 4)
  useEffect(() => {
    if (open && tipoLiberacao === 4) {
      carregarProjetistasELinhas();
    }
  }, [open, tipoLiberacao]);

  // Carregar tipologias quando projetista ou linha mudarem (tipo 4, nivel 2)
  useEffect(() => {
    if (tipoLiberacao === 4 && nivelCadastro === "nivel2" && (projetistaId || linhaId)) {
      carregarTipologias();
    }
  }, [projetistaId, linhaId, tipoLiberacao, nivelCadastro]);
  
  // Carregar Projetista + Linha/Sistema quando tipo 4, nivel 1
  // Deve exibir apenas combinações que possuem tipologias "Disponível Mercado"
  useEffect(() => {
    if (tipoLiberacao === 4 && nivelCadastro === "nivel1" && open && !isLoading) {
      const carregarProjetistasLinhas = async () => {
        try {
          console.log("Carregando Projetistas + Linha/Sistema...");
          const { data } = await apiAntigo.get("/LiberacaoPadraoCEM/ListarProjetistasLinhasDisponiveis");
          console.log("Resposta do endpoint:", data);
          
          if (data && Array.isArray(data) && data.length > 0) {
            const projetistasLinhas: DualListItem[] = data.map((item: any) => ({
              id: item.value || item.Value || "",
              label: item.label || item.Label || "",
            })).filter((item: DualListItem) => item.id && item.label); // Filtrar itens inválidos
            
            console.log("Projetistas + Linha/Sistema processados:", projetistasLinhas);
            setTipologiasDisponiveis(projetistasLinhas);
            
            // Se estiver em modo edição, carregar seleções já existentes
            if (liberacaoId) {
              await carregarTipologiasDisponiveis(liberacaoId);
            } else {
              setTipologiasSelecionadas([]);
            }
          } else {
            console.warn("Resposta inválida ou vazia do endpoint ListarProjetistasLinhasDisponiveis:", data);
            setTipologiasDisponiveis([]);
            setTipologiasSelecionadas([]);
          }
        } catch (error: any) {
          console.error("Erro ao carregar Projetistas + Linha/Sistema:", error);
          console.error("Detalhes do erro:", error.response?.data || error.message);
          openNotification({ 
            message: error.response?.data?.message || "Erro ao carregar Projetistas + Linha/Sistema. Verifique os logs para mais detalhes.", 
            type: "error" 
          });
          setTipologiasDisponiveis([]);
          setTipologiasSelecionadas([]);
        }
      };
      carregarProjetistasLinhas();
    } else if (tipoLiberacao === 4 && nivelCadastro === "nivel2") {
      // Para nivel 2, manter comportamento original (carregar tipologias quando projetista/linha mudarem)
      // Isso já é tratado pelo useEffect existente
    }
  }, [tipoLiberacao, nivelCadastro, open, isLoading, liberacaoId]);

  // Carregar aplicabilidades e tabelas ao abrir modal
  useEffect(() => {
    if (open) {
      carregarAplicabilidades();
      carregarTabelas();
    }
  }, [open]);

  // Recarregar aplicabilidades quando tipoLiberacao for 2 (em qualquer nível — reporte de 09/10/2026)
  useEffect(() => {
    if (open && tipoLiberacao === 2 && !isLoading) {
      carregarAplicabilidades();
    }
  }, [open, tipoLiberacao, isLoading]);

  // Recarregar tabelas de preço quando tipoLiberacao for 3 (em qualquer nível — reporte de 09/10/2026)
  useEffect(() => {
    if (open && tipoLiberacao === 3 && !isLoading) {
      carregarTabelas();
    }
  }, [open, tipoLiberacao, isLoading]);

  // Carregar clientes para tipo 4
  useEffect(() => {
    if (open && tipoLiberacao === 4) {
      carregarClientes();
    }
  }, [open, tipoLiberacao]);

  const carregarOpcoesIniciais = async () => {
    try {
      // Buscar tipos e sistemas da API (endpoints podem não existir, usar fallback silenciosamente)
      const [tiposResp, sistemasResp] = await Promise.all([
        apiAntigo.get("/geral/selecaoTipoLiberacao").catch((error: any) => {
          // Silenciar erro 404 pois endpoint pode não existir - usar fallback
          return { data: [] };
        }),
        apiAntigo.get("/geral/selecaoSistema").catch((error: any) => {
          // Silenciar erro 404 pois endpoint pode não existir - usar fallback
          return { data: [] };
        }),
      ]);

      // Se não retornar dados, usar fallback
      const tipos = tiposResp.data && tiposResp.data.length > 0
        ? tiposResp.data
        : [
            { value: 1, label: "Liberar cadastros nível 1" },
            { value: 2, label: "Aplicabilidades" },
            { value: 3, label: "Tabela de preço" },
            { value: 4, label: "Liberar cadastros nível 2" },
          ];

      const sistemas = sistemasResp.data && sistemasResp.data.length > 0
        ? sistemasResp.data
        : [
            { value: "1", label: "CEM" },
            { value: "2", label: "CEM Smart" },
            { value: "3", label: "CEM Mini" },
            { value: "4", label: "ONE" },
            { value: "5", label: "PAR" },
            { value: "6", label: "Pref Closed" },
            { value: "7", label: "Pref Open" },
            // Novo sistema tratato como CEM nas regras de liberação.
            { value: "8", label: "CEM MOBI" },
          ];

      setTiposOpcoes(tipos);
      setSistemasOpcoes(sistemas);
    } catch (error) {
      console.error("Erro ao carregar opções iniciais:", error);
      // Fallback em caso de erro
      setTiposOpcoes([
        { value: 1, label: "Liberar cadastros nível 1" },
        { value: 2, label: "Aplicabilidades" },
        { value: 3, label: "Tabela de preço" },
        { value: 4, label: "Liberar cadastros nível 2" },
      ]);
      setSistemasOpcoes([
        { value: "1", label: "CEM" },
        { value: "2", label: "CEM Smart" },
        { value: "3", label: "CEM Mini" },
        { value: "4", label: "ONE" },
        { value: "5", label: "PAR" },
        { value: "6", label: "Pref Closed" },
        { value: "7", label: "Pref Open" },
        { value: "8", label: "CEM MOBI" },
      ]);
    }
  };

  const resetarFormulario = () => {
    setTipoLiberacao("");
    setSistema("");
    setNivelCadastro("nivel1");
    setLinhaIdN1("");
    setProjetistaId("");
    setLinhaId("");
    setLiberarParaQuemPossui("");
    setBuscaN1("");
    setBuscaN2("");
    setBuscaAplic("");
    setBuscaTabela("");
    setBuscaCliente("");
    setPacotesDisponiveis([]);
    setPacotesSelecionados([]);
    setTipologiasDisponiveis([]);
    setTipologiasSelecionadas([]);
    setAplicabilidadesDisponiveis([]);
    setAplicabilidadesSelecionadas([]);
    setTabelasDisponiveis([]);
    setTabelasSelecionadas([]);
    setClientesDisponiveis([]);
    setClientesSelecionados([]);
    liberacaoCarregadaRef.current = null;
    pacotesOcultosRef.current = [];
    referenciasGravadasRef.current = new Set();
    listasAlteradasRef.current = new Set();
    idCriadoRef.current = null;
  };

  const carregarDadosLiberacao = async () => {
    if (!liberacaoId) return;
    // O modal fica montado entre aberturas: nada da liberação anterior pode sobrar
    liberacaoCarregadaRef.current = null;
    pacotesOcultosRef.current = [];
    referenciasGravadasRef.current = new Set();
    listasAlteradasRef.current = new Set();
    idCriadoRef.current = null;
    setNivelCadastro("nivel1");
    setLiberarParaQuemPossui("");
    setIsLoading(true);
    try {
      const { data } = await apiAntigo.get(`/LiberacaoDiversa/Liberacao/${liberacaoId}`);

      setTipoLiberacao(data.tipo?.value || "");
      setSistema(data.sistema?.value?.toString() || "");

      liberacaoCarregadaRef.current = data;

      // Restaurar o "Liberar para quem possui" (IdAtualizacaoCEMExistente) e o nível pelo tipo do
      // pacote salvo: pacote de tipologia (Projetista + Linha) = Cadastro nível 2. Tipo 4 sem
      // "quem possui" é o layout de nível 2 (Projetista/Linha na própria seção).
      const { data: estado } = await api.get(`LiberacaoDiversaPacotes/${liberacaoId}`);
      if (estado?.quemPossui) {
        setNivelCadastro(estado.quemPossui.tipoPacote === 0 ? "nivel2" : "nivel1");
        setLiberarParaQuemPossui(estado.quemPossui.id);
      } else if (data.tipo?.value === 4) {
        setNivelCadastro("nivel2");
      }

      // Restaurar o linhaIdN1 (linha) se existir (mantido para compatibilidade)
      if (data.idReferencia || data.IdReferencia || data.idLinha || data.IdLinha) {
        setLinhaIdN1(data.idReferencia || data.IdReferencia || data.idLinha || data.IdLinha);
      }

      // Restaurar projetistaId e linhaId para tipo 4
      if (data.tipo?.value === 4) {
        if (data.idProjetista || data.IdProjetista) {
          setProjetistaId(data.idProjetista || data.IdProjetista);
        }
        if (data.idLinha || data.IdLinha) {
          setLinhaId(data.idLinha || data.IdLinha);
        }
      }

      // Carregar listas específicas por tipo
      if (data.tipo?.value === 1) {
        // Produtos (N1) - será carregado pelo useEffect quando liberarParaQuemPossui for setado
      } else if (data.tipo?.value === 2) {
        // Aplicabilidades
        await carregarAplicabilidadesDisponiveis(liberacaoId);
      } else if (data.tipo?.value === 3) {
        // Tabelas
        await carregarTabelasDisponiveis(liberacaoId);
      } else if (data.tipo?.value === 4) {
        // N2 - Tipologias e Clientes
        await carregarTipologiasDisponiveis(liberacaoId);
        await carregarClientesDisponiveis(liberacaoId);
      }
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const carregarOpcoesQuemPossui = async (nivel: "nivel1" | "nivel2") => {
    const pedido = ++pedidoQuemPossuiRef.current;
    const ultimoPedido = () => pedido === pedidoQuemPossuiRef.current;
    setCarregandoQuemPossui(true);
    try {
      const { data } = await api.get("LiberacaoDiversaPacotes/QuemPossui", {
        params: { nivel: nivel === "nivel2" ? 2 : 1 },
      });
      // Troca rápida de nível: só vale a resposta do último pedido
      if (ultimoPedido()) setOpcoesQuemPossui(data || []);
    } catch (error: any) {
      console.error("Erro ao carregar 'Liberar para quem possui':", error);
      if (ultimoPedido()) {
        setOpcoesQuemPossui([]);
        openNotification({
          message: "Não foi possível carregar 'Liberar para quem possui'. Tente novamente.",
          type: "error",
        });
      }
    } finally {
      if (ultimoPedido()) setCarregandoQuemPossui(false);
    }
  };

  const carregarProjetistasELinhas = async () => {
    try {
      const [projetistasResp, linhasResp] = await Promise.all([
        apiAntigo.get("/geral/selecaoProjetista"),
        apiAntigo.get("/geral/selecaoLinha"),
      ]);
      setProjetistasOpcoes(projetistasResp.data || []);
      setLinhasN2Opcoes(linhasResp.data || []);
    } catch (error: any) {
      console.error("Erro ao carregar projetistas/linhas:", error);
    }
  };

  const carregarTipologias = async () => {
    try {
      const params: any = {};
      // Só aplicar filtros se for nivel 2
      if (nivelCadastro === "nivel2") {
      if (projetistaId) params.projetistaId = projetistaId;
      if (linhaId) params.linhaId = linhaId;
      }

      const { data } = await apiAntigo.get("/LiberacaoPadraoCEM/listarTipologias", { params });
      
      const tipologias: DualListItem[] = (data || []).map((t: any) => ({
        id: t.id || t.Id || t.idTipologia || t.IdTipologia,
        label: t.descricao || t.Descricao || t.nome || t.Nome || "",
        subtitle: t.codigo || t.Codigo || undefined,
      }));

      setTipologiasDisponiveis(tipologias);
    } catch (error: any) {
      console.error("Erro ao carregar tipologias:", error);
    }
  };

  const carregarAplicabilidades = async () => {
    try {
      const { data } = await apiAntigo.get("/geral/referenciaAplicabilidadeExistente");
      
      // O endpoint retorna { Label: string (Fantasia da empresa), Value: int (IdEmpresaAplicabilidade) }
      const aplicabilidades: DualListItem[] = (data || []).map((a: any) => ({
        id: a.value !== undefined ? a.value : (a.Value !== undefined ? a.Value : (a.id || a.Id || a.idEmpresaAplicabilidade || a.IdEmpresaAplicabilidade)),
        label: a.label !== undefined ? a.label : (a.Label !== undefined ? a.Label : (a.nomeReferencia || a.NomeReferencia || a.descricao || a.Descricao || "")),
      })).filter((item: DualListItem) => item.id && item.label); // Filtrar itens inválidos

      setAplicabilidadesDisponiveis(aplicabilidades);
    } catch (error: any) {
      console.error("Erro ao carregar aplicabilidades:", error);
      setAplicabilidadesDisponiveis([]);
    }
  };

  const carregarTabelas = async () => {
    try {
      const { data } = await apiAntigo.get("/geral/SelecaoTabelaPrecoDisponivelDasEmpresas");
      
      // O endpoint retorna { Value: int (IdImportacaoTabelaPreco), Label: string (Descricao da tabela) }
      const tabelas: DualListItem[] = (data || []).map((t: any) => ({
        id: t.value !== undefined ? t.value : (t.Value !== undefined ? t.Value : (t.id || t.Id || t.idTabelaPreco || t.IdTabelaPreco)),
        label: t.label !== undefined ? t.label : (t.Label !== undefined ? t.Label : (t.descricao || t.Descricao || t.nome || t.Nome || "")),
      })).filter((item: DualListItem) => item.id && item.label); // Filtrar itens inválidos

      setTabelasDisponiveis(tabelas);
    } catch (error: any) {
      console.error("Erro ao carregar tabelas:", error);
      setTabelasDisponiveis([]);
    }
  };

  const carregarClientes = async () => {
    try {
      const { data } = await apiAntigo.get("/LiberacaoDiversa/ListarSeriaisCEM");
      
      const clientes: DualListItem[] = (data || []).map((c: any) => ({
        id: c.serial || c.Serial,
        label: `${c.nomeFantasia || c.NomeFantasia || ""} - ${c.serial || c.Serial || ""}`,
      }));

      setClientesDisponiveis(clientes);
    } catch (error: any) {
      console.error("Erro ao carregar clientes:", error);
    }
  };

  const carregarPacotesDisponiveis = async (idLiberacao: number) => {
    const linhaIdParaUsar = liberarParaQuemPossui || linhaIdN1;
    if (!linhaIdParaUsar) return;
    try {
      // Buscar lista completa de pacotes (Tipo + Referência + Linha)
      // Para CEM, usar SelecaoPacoteComLinha que retorna os mesmos dados do dropdown "Liberar para quem possui"
      // Para PrefSuite, manter o endpoint original
      const endpointLista = isPrefSuite ? "/geral/SelecaoPacotePrefsuite" : "/geral/SelecaoPacoteComLinha";
      const { data: todosPacotes } = await apiAntigo.get(endpointLista);

      // Mapear todos os pacotes primeiro
      const todosMapeados: DualListItem[] = (todosPacotes || []).map((p: any) => ({
        id: p.value || p.Value,
        label: p.label || p.Label || "",
      }));

      // Debug temporário: verificar formato dos primeiros labels
      console.log("🔍 DEBUG - Carregando pacotes (carregarPacotesDisponiveis):");
      console.log("  - Total recebido da API:", todosMapeados.length);
      if (todosMapeados.length > 0) {
        console.log("  - Primeiros 10 labels recebidos:", todosMapeados.slice(0, 10).map(p => `"${p.label}"`));
      } else {
        console.warn("  - ⚠️ Nenhum pacote recebido da API!");
      }

      // Filtrar para excluir Tipologia (TipoPacote = 0) - manter apenas Referência (1), Linha (2) e Acabamento (3)
      // O label é formatado como: "TIPOLOGIA REFERENCIA LINHA" ou "REFERÊNCIA REFERENCIA" etc.
      // IMPORTANTE: TipoPacote enum: 0=TIPOLOGIA, 1=REFERÊNCIA, 2=LINHA, 3=ACABAMENTO
      const disponiveis: DualListItem[] = todosMapeados.filter((item: DualListItem) => {
        if (!item.label || !item.id) {
          return false;
        }
        
        const label = item.label.trim();
        if (!label) {
          return false;
        }
        
        // Converter para maiúsculas para comparação case-insensitive
        const labelUpper = label.toUpperCase();
        
        // Excluir Tipologia (TipoPacote = 0) - que começa com "TIPOLOGIA"
        if (labelUpper.startsWith("TIPOLOGIA")) {
          return false;
        }
        
        // Incluir se começar com Referência (1), Linha (2) ou Acabamento (3)
        // Verificar com e sem acento para Referência (o backend pode retornar "REFERÊNCIA" ou "REFERENCIA" dependendo da cultura)
        const isReferencia = labelUpper.startsWith("REFERÊNCIA") || labelUpper.startsWith("REFERENCIA");
        const isLinha = labelUpper.startsWith("LINHA");
        const isAcabamento = labelUpper.startsWith("ACABAMENTO");
        
        return isReferencia || isLinha || isAcabamento;
      });
      
      // Debug temporário: verificar quantos itens passaram no filtro
      console.log(`  - ✅ Total após filtro: ${disponiveis.length} de ${todosMapeados.length}`);
      if (disponiveis.length > 0) {
        console.log("  - Primeiros 5 labels após filtro:", disponiveis.slice(0, 5).map(p => `"${p.label}"`));
      } else {
        console.warn("  - ⚠️ Nenhum item passou no filtro!");
      }

      // Buscar pacotes já selecionados
      const endpointSelecionados = isPrefSuite
        ? `/LiberacaoDiversa/LiberacaoPortfolioPrefSuiteDisponivel/${idLiberacao}/${linhaIdParaUsar}`
        : `/LiberacaoDiversa/LiberacaoPortfolioDisponivel/${idLiberacao}/${linhaIdParaUsar}`;
        
      const { data: selectedData } = await apiAntigo.get(endpointSelecionados);
      
      const idsSelected = selectedData.selectedOption || selectedData || [];
      const selecionados: DualListItem[] = disponiveis.filter((d) => idsSelected.includes(d.id));

      setPacotesDisponiveis(disponiveis);
      setPacotesSelecionados(selecionados);
    } catch (error: any) {
      console.error("Erro ao carregar pacotes disponíveis:", error);
    }
  };
  
  // Tipo 1, CEM: pacotes N1 de mercado (referência, linha, acabamento) em qualquer nível — o nível é
  // só parâmetro do "quem possui" (reporte de 09/10/2026). Vem do MOF novo com a referência de cada
  // pacote, que é a chave pela qual o legado grava (salvarPacotes).
  useEffect(() => {
    if (!(tipoLiberacao === 1 && !isPrefSuite && open && !isLoading)) return;
    let ativo = true;

    const carregarPacotesN1 = async () => {
      try {
        // Conteúdo marcável: TODOS os pacotes N1 — o QuemPossui passou a trazer só os que já têm
        // serial (plano de 14/10/2026, Frente 3)
        const { data } = await api.get("LiberacaoDiversaPacotes/PacotesN1");
        const disponiveis: DualListItem[] = (data || []).map((p: PacoteQuemPossui) => ({
          id: p.id,
          label: p.label,
          idReferencia: p.idReferencia,
        }));

        let selecionados: DualListItem[] = [];
        let ocultos: DualListItem[] = [];
        let referenciasGravadas = new Set<number>();

        if (liberacaoId) {
          const { data: estado } = await api.get(`LiberacaoDiversaPacotes/${liberacaoId}`);
          const gravados: PacoteGravado[] = estado?.pacotesSelecionados || [];
          const idsGravados = new Set(gravados.map((g) => g.idAtualizacaoPortfolioCEM));
          const idsVisiveis = new Set(disponiveis.map((d) => d.id));

          selecionados = disponiveis.filter((d) => idsGravados.has(Number(d.id)));
          ocultos = gravados
            .filter((g) => !idsVisiveis.has(g.idAtualizacaoPortfolioCEM))
            .map((g) => ({ id: g.idAtualizacaoPortfolioCEM, label: "", idReferencia: g.idReferencia }));
          referenciasGravadas = new Set(
            gravados.map((g) => g.idReferencia).filter((r): r is number => r !== null && r !== undefined)
          );
        }

        if (!ativo) return;
        pacotesOcultosRef.current = ocultos;
        referenciasGravadasRef.current = referenciasGravadas;
        setPacotesDisponiveis(disponiveis);
        setPacotesSelecionados(selecionados);
      } catch (error) {
        console.error("Erro ao carregar pacotes N1:", error);
      }
    };

    carregarPacotesN1();
    return () => {
      ativo = false;
    };
  }, [tipoLiberacao, isPrefSuite, open, isLoading, liberacaoId]);

  // Tipo 1, PrefSuite: caminho anterior (fora do escopo do plano de 09/10/2026)
  // A lista sempre mostra TODOS os pacotes disponíveis (Tipo + Referência + Linha)
  useEffect(() => {
    // Debug: verificar condições do useEffect
    console.log("🔍 DEBUG - Verificando condições do useEffect:", {
      tipoLiberacao,
      sistema,
      nivelCadastro,
      open,
      isLoading,
      todasCondicoesOk: tipoLiberacao === 1 && nivelCadastro === "nivel1" && open && !isLoading
    });
    
    if (tipoLiberacao === 1 && isPrefSuite && open && !isLoading) {
      const carregarPacotesCompletos = async () => {
        try {
          // Para CEM, usar SelecaoPacoteComLinha que retorna os mesmos dados do dropdown "Liberar para quem possui"
          // Para PrefSuite, manter o endpoint original
          // Se sistema não estiver selecionado, usar CEM como padrão
          const sistemaParaUsar = sistema || "1"; // Default para CEM se não selecionado
          const isPrefSuiteParaUsar = ["6", "7"].includes(sistemaParaUsar);
          const endpointLista = isPrefSuiteParaUsar ? "/geral/SelecaoPacotePrefsuite" : "/geral/SelecaoPacoteComLinha";
          const { data: todosPacotes } = await apiAntigo.get(endpointLista);
          
          // Mapear todos os pacotes primeiro
          const todosMapeados: DualListItem[] = (todosPacotes || []).map((p: any) => ({
            id: p.value || p.Value,
            label: p.label || p.Label || "",
          }));
          
          // Debug temporário: verificar formato dos primeiros labels
          console.log("🔍 DEBUG - Carregando pacotes (useEffect):");
          console.log("  - Total recebido da API:", todosMapeados.length);
          if (todosMapeados.length > 0) {
            console.log("  - Primeiros 10 labels recebidos:", todosMapeados.slice(0, 10).map(p => `"${p.label}"`));
          } else {
            console.warn("  - ⚠️ Nenhum pacote recebido da API!");
          }
          
          // Filtrar para excluir Tipologia (TipoPacote = 0) - manter apenas Referência (1), Linha (2) e Acabamento (3)
          // O label é formatado pelo backend como: "TIPOLOGIA REFERENCIA LINHA" ou "REFERÊNCIA REFERENCIA" etc.
          // GetDisplayName() retorna: "Tipologia", "Referência", "Linha", "Acabamento" (depois convertido para maiúsculas)
          // IMPORTANTE: TipoPacote enum: 0=TIPOLOGIA, 1=REFERÊNCIA, 2=LINHA, 3=ACABAMENTO
          const disponiveis: DualListItem[] = todosMapeados.filter((item: DualListItem) => {
            if (!item.label || !item.id) {
              return false;
            }
            
            const label = item.label.trim();
            if (!label) {
              return false;
            }
            
            // Converter para maiúsculas para comparação case-insensitive
            const labelUpper = label.toUpperCase();
            
            // Excluir Tipologia (TipoPacote = 0) - que começa com "TIPOLOGIA"
            if (labelUpper.startsWith("TIPOLOGIA")) {
              return false;
            }
            
            // Incluir se começar com Referência (1), Linha (2) ou Acabamento (3)
            // Verificar com e sem acento para Referência (o backend pode retornar "REFERÊNCIA" ou "REFERENCIA" dependendo da cultura)
            const isReferencia = labelUpper.startsWith("REFERÊNCIA") || labelUpper.startsWith("REFERENCIA");
            const isLinha = labelUpper.startsWith("LINHA");
            const isAcabamento = labelUpper.startsWith("ACABAMENTO");
            
            const isIncluded = isReferencia || isLinha || isAcabamento;
            
            // Debug apenas para os primeiros itens que não passaram no filtro
            if (!isIncluded && todosMapeados.indexOf(item) < 5) {
              console.log(`  - Item não incluído: "${label}" (normalizado: "${labelUpper}")`);
            }
            
            return isIncluded;
          });
          
          // Debug temporário: verificar quantos itens passaram no filtro
          console.log(`  - ✅ Total após filtro: ${disponiveis.length} de ${todosMapeados.length}`);
          if (disponiveis.length > 0) {
            console.log("  - Primeiros 5 labels após filtro:", disponiveis.slice(0, 5).map(p => `"${p.label}"`));
          } else {
            console.warn("  - ⚠️ Nenhum item passou no filtro!");
            // Mostrar os primeiros 5 labels originais para debug
            if (todosMapeados.length > 0) {
              console.log("  - Primeiros 5 labels originais:", todosMapeados.slice(0, 5).map(p => `"${p.label}"`));
            }
          }
          
          setPacotesDisponiveis(disponiveis);
          
          // Se estiver em modo edição e tiver linha selecionada, carregar pacotes já selecionados
          if (liberacaoId && (liberarParaQuemPossui || linhaIdN1)) {
            const linhaIdParaUsar = liberarParaQuemPossui || linhaIdN1;
            const endpointSelecionados = isPrefSuiteParaUsar
              ? `/LiberacaoDiversa/LiberacaoPortfolioPrefSuiteDisponivel/${liberacaoId}/${linhaIdParaUsar}`
              : `/LiberacaoDiversa/LiberacaoPortfolioDisponivel/${liberacaoId}/${linhaIdParaUsar}`;
            
            try {
              const { data: selectedData } = await apiAntigo.get(endpointSelecionados);
              const idsSelected = selectedData.selectedOption || selectedData || [];
              const selecionados: DualListItem[] = disponiveis.filter((d) => idsSelected.includes(d.id));
              setPacotesSelecionados(selecionados);
            } catch (error) {
              console.error("Erro ao carregar pacotes selecionados:", error);
              setPacotesSelecionados([]);
            }
          } else {
            setPacotesSelecionados([]);
          }
        } catch (error) {
          console.error("Erro ao carregar pacotes:", error);
        }
      };
      carregarPacotesCompletos();
    }
  }, [tipoLiberacao, sistema, isPrefSuite, nivelCadastro, open, isLoading, liberacaoId, liberarParaQuemPossui, linhaIdN1]);

  const carregarTipologiasDisponiveis = async (idLiberacao: number) => {
    try {
      // Se for nivel 1 e tipo 4, carregar Projetista + Linha/Sistema
      if (nivelCadastro === "nivel1" && tipoLiberacao === 4) {
        // Buscar lista completa de Projetista + Linha/Sistema
        const { data: todasProjetistasLinhas } = await apiAntigo.get("/LiberacaoPadraoCEM/ListarProjetistasLinhasDisponiveis");
        if (todasProjetistasLinhas && Array.isArray(todasProjetistasLinhas)) {
          const disponiveis: DualListItem[] = todasProjetistasLinhas.map((item: any) => ({
            id: item.value || item.Value || "",
            label: item.label || item.Label || "",
          }));

          // Buscar Projetista + Linha já liberados
          try {
            const { data: selectedData } = await apiAntigo.get(`/LiberacaoPadraoCEM/ListarProjetistasLinhasLiberados/${idLiberacao}`);
            const idsProjetistasLinhasSelected = selectedData?.selectedProjetistasLinhas || selectedData || [];
            const selecionados: DualListItem[] = disponiveis.filter((d) => 
              idsProjetistasLinhasSelected.includes(String(d.id)) || idsProjetistasLinhasSelected.includes(d.id)
            );
            setTipologiasSelecionadas(selecionados);
          } catch (error) {
            console.error("Erro ao carregar Projetistas + Linha liberados:", error);
            setTipologiasSelecionadas([]);
          }

          setTipologiasDisponiveis(disponiveis);
        } else {
          console.warn("Resposta inválida do endpoint ListarProjetistasLinhasDisponiveis:", todasProjetistasLinhas);
          setTipologiasDisponiveis([]);
          setTipologiasSelecionadas([]);
        }
      } else {
        // Para nivel 2, manter comportamento original
        const params: any = {};
        if (projetistaId) params.projetistaId = projetistaId;
        if (linhaId) params.linhaId = linhaId;

        const { data: todasTipologias } = await apiAntigo.get("/LiberacaoPadraoCEM/ListarTipologias", { params });

        const disponiveis: DualListItem[] = (todasTipologias || []).map((t: any) => ({
          id: t.value || t.Value,
          label: t.label || t.Label || "",
        }));

        // Buscar tipologias já selecionadas
        const { data: selectedData } = await apiAntigo.get(`/LiberacaoDiversa/LiberacaoTipologiaDisponivel/${idLiberacao}`);
        
        const idsSelected = selectedData.selectedTipologias || [];
        const selecionados: DualListItem[] = disponiveis.filter((d) => idsSelected.includes(d.id));

        setTipologiasDisponiveis(disponiveis);
        setTipologiasSelecionadas(selecionados);
      }
    } catch (error: any) {
      console.error("Erro ao carregar tipologias disponíveis:", error);
    }
  };

  const carregarAplicabilidadesDisponiveis = async (idLiberacao: number) => {
    try {
      // Buscar lista completa de aplicabilidades
      // O endpoint retorna { Label: string (Fantasia da empresa), Value: int (IdEmpresaAplicabilidade) }
      const { data: todasAplicabilidades } = await apiAntigo.get("/geral/ReferenciaAplicabilidadeExistente");

      const disponiveis: DualListItem[] = (todasAplicabilidades || []).map((a: any) => ({
        id: a.value !== undefined ? a.value : (a.Value !== undefined ? a.Value : (a.id || a.Id || a.idEmpresaAplicabilidade || a.IdEmpresaAplicabilidade)),
        label: a.label !== undefined ? a.label : (a.Label !== undefined ? a.Label : (a.descricao || a.Descricao || "")),
      })).filter((item: DualListItem) => item.id && item.label); // Filtrar itens inválidos

      // Buscar aplicabilidades já selecionadas
      const { data: selectedData } = await apiAntigo.get(`/LiberacaoDiversa/LiberacaoAplicabilidadeDisponivel/${idLiberacao}`);
      
      const idsSelected = selectedData.selectedAplicabilidade || selectedData || [];
      const selecionados: DualListItem[] = disponiveis.filter((d) => idsSelected.includes(d.id));

      setAplicabilidadesDisponiveis(disponiveis);
      setAplicabilidadesSelecionadas(selecionados);
    } catch (error: any) {
      console.error("Erro ao carregar aplicabilidades disponíveis:", error);
      setAplicabilidadesDisponiveis([]);
      setAplicabilidadesSelecionadas([]);
    }
  };

  const carregarTabelasDisponiveis = async (idLiberacao: number) => {
    try {
      // Buscar lista completa de tabelas
      // O endpoint retorna { Value: int (IdImportacaoTabelaPreco), Label: string (Descricao da tabela) }
      const { data: todasTabelas } = await apiAntigo.get("/geral/SelecaoTabelaPrecoDisponivelDasEmpresas");

      const disponiveis: DualListItem[] = (todasTabelas || []).map((t: any) => ({
        id: t.value !== undefined ? t.value : (t.Value !== undefined ? t.Value : (t.id || t.Id || t.idTabelaPreco || t.IdTabelaPreco)),
        label: t.label !== undefined ? t.label : (t.Label !== undefined ? t.Label : (t.descricao || t.Descricao || t.nome || t.Nome || "")),
      })).filter((item: DualListItem) => item.id && item.label); // Filtrar itens inválidos

      // Buscar tabelas já selecionadas
      const { data: selectedData } = await apiAntigo.get(`/LiberacaoDiversa/LiberacaoTabelaPrecoDisponivel/${idLiberacao}`);
      
      const idsSelected = selectedData.selectedTabela || selectedData || [];
      const selecionados: DualListItem[] = disponiveis.filter((d) => idsSelected.includes(d.id));

      setTabelasDisponiveis(disponiveis);
      setTabelasSelecionadas(selecionados);
    } catch (error: any) {
      console.error("Erro ao carregar tabelas disponíveis:", error);
      setTabelasDisponiveis([]);
      setTabelasSelecionadas([]);
    }
  };

  const carregarClientesDisponiveis = async (idLiberacao: number) => {
    try {
      // Buscar lista completa de seriais CEM
      const { data: todosSeriais } = await apiAntigo.get("/LiberacaoDiversa/ListarSeriaisCEM");

      const disponiveis: DualListItem[] = (todosSeriais || []).map((c: any) => ({
        id: c.serial || c.Serial,
        label: c.serial || c.Serial || "",
        subtitle: c.inquilinoId || c.InquilinoId || undefined,
      }));

      // Buscar clientes já selecionados
      const { data: selectedData } = await apiAntigo.get(`/LiberacaoDiversa/LiberacaoClienteDisponivel/${idLiberacao}`);
      
      const clientesSelected = selectedData.selectedClientes || selectedData || [];
      
      // Match por serial
      const selecionados: DualListItem[] = disponiveis.filter((d) => 
        clientesSelected.some((c: any) => (c.serial || c.Serial) === d.id)
      );

      setClientesDisponiveis(disponiveis);
      setClientesSelecionados(selecionados);
    } catch (error: any) {
      console.error("Erro ao carregar clientes disponíveis:", error);
    }
  };

  // Plano 21/10/2026 (Frente 8): marcar uma lista só registra a alteração; a gravação é no Salvar.
  const marcarAlterada = (lista: ListaEditavel) => {
    if (modoLeituraRef.current) return;
    listasAlteradasRef.current.add(lista);
  };

  // idLiberacao: na criação o modal ainda não tem liberacaoId — handleSalvar passa o id recém-criado.
  // Plano 21/10/2026 (Frente 1): as gravações repassam o erro a quem chama — antes o erro era
  // engolido aqui e o Salvar dizia "salva com sucesso" mesmo sem gravar a lista.
  const salvarPacotes = async (selection: DualListItem[], idLiberacao: number | null = liberacaoId) => {
    if (!idLiberacao) return;
    if (!isCEM) {
      // PrefSuite: caminho anterior (fora do escopo do plano de 09/10/2026)
      const linhaIdParaUsar = liberarParaQuemPossui || linhaIdN1;
      if (!linhaIdParaUsar) return;
      await apiAntigo.post("/LiberacaoDiversa/liberacaoPortfolioPrefSuite", {
        idLiberacaoDiversa: idLiberacao,
        idReferencia: linhaIdParaUsar,
        idsAtualizacaoPortfolio: selection.map((p) => p.id),
      });
      return;
    }

    // O legado grava por referência: apaga as linhas da (liberação, referência) e insere as enviadas.
    // Por isso agrupa pela referência do próprio pacote, reenvia vazias as referências que ficaram sem
    // pacote e devolve junto os pacotes gravados que a lista não mostra.
    const porReferencia = new Map<number, (number | string)[]>();
    referenciasGravadasRef.current.forEach((idReferencia) => porReferencia.set(idReferencia, []));
    [...selection, ...pacotesOcultosRef.current].forEach((pacote) => {
      if (pacote.idReferencia === null || pacote.idReferencia === undefined) return;
      porReferencia.set(pacote.idReferencia, [...(porReferencia.get(pacote.idReferencia) ?? []), pacote.id]);
    });

    // Referências esvaziadas primeiro: um pacote que muda de referência é apagado da antiga antes de
    // entrar na nova (índice único da LiberacaoDiversaPortfolio)
    const envios = Array.from(porReferencia).sort(([, a], [, b]) => a.length - b.length);
    for (const [idReferencia, idsAtualizacaoPortfolio] of envios) {
      await apiAntigo.post("/LiberacaoDiversa/liberacaoPortfolioCEM", {
        idLiberacaoDiversa: idLiberacao,
        idReferencia,
        idsAtualizacaoPortfolio,
      });
    }

    referenciasGravadasRef.current = new Set(
      Array.from(porReferencia)
        .filter(([, ids]) => ids.length > 0)
        .map(([idReferencia]) => idReferencia)
    );
  };


  const salvarTipologias = async (selection: DualListItem[], idLiberacao: number | null = liberacaoId) => {
    if (!idLiberacao) return;
    // Quando nivelCadastro === "nivel1", selection contém Projetista + Linha (formato "ProjetistaId_LinhaId")
    // Precisamos extrair ProjetistaId e LinhaId e buscar todas as tipologias dessa combinação
    let idsTipologia: number[] = [];
    let idProjetistaFinal: number | null = projetistaId || null;
    let idLinhaFinal: number | null = linhaId || null;

    if (nivelCadastro === "nivel1" && tipoLiberacao === 4) {
      // Para nivel 1, selection contém strings no formato "ProjetistaId_LinhaId"
      // Precisamos buscar todas as tipologias dessas combinações
      const tipologiasPromises = selection.map(async (item) => {
        const [projetistaIdStr, linhaIdStr] = String(item.id).split("_");
        const projetistaIdNum = parseInt(projetistaIdStr, 10);
        const linhaIdNum = parseInt(linhaIdStr, 10);
        
        if (!isNaN(projetistaIdNum) && !isNaN(linhaIdNum)) {
          const { data } = await apiAntigo.get("/LiberacaoPadraoCEM/listarTipologias", {
            params: { projetistaId: projetistaIdNum, linhaId: linhaIdNum }
          });
          // Plano 21/10/2026 (Frente 1): o endpoint devolve { value, label } (o mesmo que a lista de
          // disponíveis lê); com t.id iam só nulos e a API recusava a gravação inteira.
          return (data || []).map((t: any) => t.value ?? t.Value).filter((id: any) => Number.isInteger(id));
        }
        return [];
      });
      
      const tipologiasArrays = await Promise.all(tipologiasPromises);
      idsTipologia = tipologiasArrays.flat();
      
      // Para nivel 1, não definimos projetista/linha específicos (liberamos todas as tipologias da combinação)
      idProjetistaFinal = null;
      idLinhaFinal = null;
    } else {
      // Para nivel 2, mantém comportamento original (tipologias individuais)
      idsTipologia = selection.map((t) => Number(t.id));
    }

    const payload = {
      idLiberacaoDiversa: idLiberacao,
      idsTipologia: idsTipologia,
      idProjetista: idProjetistaFinal,
      idLinha: idLinhaFinal,
    };

    await apiAntigo.post("/LiberacaoDiversa/liberacaoTipologia", payload);
  };

  const salvarAplicabilidades = async (selection: DualListItem[], idLiberacao: number | null = liberacaoId) => {
    if (!idLiberacao) return;
    const payload = {
      idLiberacaoDiversa: idLiberacao,
      idsEmpresas: selection.map((a) => a.id),
    };

    await apiAntigo.post("/LiberacaoDiversa/liberacaoAplicabilidade", payload);
  };

  const salvarTabelas = async (selection: DualListItem[], idLiberacao: number | null = liberacaoId) => {
    if (!idLiberacao) return;
    const payload = {
      idLiberacaoDiversa: idLiberacao,
      idsTabelaPreco: selection.map((t) => t.id),
    };

    await apiAntigo.post("/LiberacaoDiversa/liberacaoTabelaPreco", payload);
  };

  const salvarClientes = async (selection: DualListItem[], idLiberacao: number | null = liberacaoId) => {
    if (!idLiberacao) return;
    const payload = {
      idLiberacaoDiversa: idLiberacao,
      seriais: selection.map((c) => c.id),
      inquilinoIds: [],
    };

    await apiAntigo.post("/LiberacaoDiversa/liberacaoCliente", payload);
  };

  const handleSalvar = async () => {
    if (modoLeitura) return;
    // Validações
    if (!tipoLiberacao || !sistema) {
      openNotification({ message: "Preencha Tipo e Sistema.", type: "error" });
      return;
    }
    
    // "Liberar para quem possui" obrigatório nos dois níveis (plano de 09/10/2026, T2): vazio, o
    // Disponibilizar do MOF antigo libera para TODOS os seriais do sistema
    if (exigeQuemPossui && !liberarParaQuemPossui) {
      openNotification({ message: "Preencha 'Liberar para quem possui'.", type: "error" });
      return;
    }

    // Plano 21/10/2026 (Frente 8): onde o Sim vai disponibilizar (Liberar CEM, sistemas 1-5, ainda
    // não disponível — as mesmas condições do ícone Disponibilizar), o Salvar pergunta antes quantos
    // clientes serão afetados, sem gravar nada. Fora disso, grava como antes.
    if (gravandoRef.current) return;
    if (usaAvisoClientes()) {
      // Sem item marcado o Disponibilizar recusa ("Nada a liberar") — avisa antes de perguntar.
      const itensDoTipo: Record<number, DualListItem[]> = {
        1: pacotesSelecionados,
        2: aplicabilidadesSelecionadas,
        3: tabelasSelecionadas,
        4: tipologiasSelecionadas,
      };
      if ((itensDoTipo[Number(tipoLiberacao)] ?? []).length === 0) {
        openNotification({ message: "Marque ao menos um item para liberar.", type: "error" });
        return;
      }
      // Na edição com a lista de clientes intocada, a prévia lê os clientes gravados (a tela só
      // carrega parte dos seriais e mostraria um número menor que o real).
      const clientesDoBanco = !!liberacaoId && !listasAlteradasRef.current.has("clientes");
      gravandoRef.current = true;
      setMensagemAndamento("Calculando os clientes afetados…");
      setSalvando(true);
      try {
        const { data } = await api.post<ClientesAfetados>("LiberacaoDiversaPacotes/ClientesAfetados", {
          tipo: Number(tipoLiberacao),
          status: 1,
          sistema: Number(sistema),
          idAtualizacaoCEMExistente: exigeQuemPossui && liberarParaQuemPossui ? Number(liberarParaQuemPossui) : null,
          idLiberacaoDiversa: clientesDoBanco ? liberacaoId : null,
          seriaisClientes:
            tipoLiberacao === 4 && !clientesDoBanco ? clientesSelecionados.map((c) => String(c.id)) : null,
        });
        setMensagemAndamento("Buscando os nomes dos clientes…");
        await carregarNomesDoAviso(data);
        setAvisoClientes(data);
      } catch (error: any) {
        mostrarErro(error);
      } finally {
        gravandoRef.current = false;
        setSalvando(false);
      }
      return;
    }

    gravandoRef.current = true;
    setMensagemAndamento("Salvando a liberação…");
    setSalvando(true);
    try {
      await gravarLiberacao();
      openNotification({
        message: "Liberação diversa salva com sucesso!",
        type: "success",
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      mostrarFalhaGravacao(error);
    } finally {
      gravandoRef.current = false;
      setSalvando(false);
    }
  };

  // Falha no meio da gravação de uma liberação nova: o cabeçalho já existe (Pendente no grid) e a
  // próxima tentativa reaproveita o mesmo id.
  const mostrarFalhaGravacao = (error: any) => {
    if (!liberacaoId && idCriadoRef.current) {
      openNotification({
        message: `${mensagemDoErro(error)} A liberação ficou gravada como Pendente; corrija e salve de novo.`,
        type: "error",
      });
      if (onSuccess) onSuccess();
      return;
    }
    mostrarErro(error);
  };

  const mensagemDoErro = (error: any): string => {
    try {
      getErrorsByApi(error);
    } catch (e: any) {
      return e.message;
    }
    return "Erro inesperado";
  };

  const mostrarErro = (error: any) => openNotification({ message: mensagemDoErro(error), type: "error" });

  const usaAvisoClientes = () => {
    const liberacaoCarregada = liberacaoCarregadaRef.current;
    const statusLiberarCem = liberacaoId
      ? [1, "LIBERAR_CEM"].includes(liberacaoCarregada?.liberacao?.value)
      : ["1", "2", "3", "4", "5", "8"].includes(String(sistema));
    return statusLiberarCem && Number(sistema) >= 1 && Number(sistema) <= 5 && !liberacaoJaDisponivel;
  };

  // "Sim" do aviso: grava a liberação e disponibiliza (decisão P5 de 21/10/2026).
  const handleConfirmarAvisoClientes = async () => {
    if (gravandoRef.current) return;
    gravandoRef.current = true;
    setMensagemAndamento(`Liberando para ${avisoClientes?.totalSeriais ?? 0} clientes, aguarde…`);
    setSalvando(true);
    let idGravado: number | null = null;
    try {
      idGravado = await gravarLiberacao();
      const { data } = await api.post<{ totalSeriais?: number }>(`LiberacaoDiversaPacotes/Disponibilizar/${idGravado}`);
      openNotification({
        message: `Liberação realizada para ${data?.totalSeriais ?? 0} cliente(s).`,
        type: "success",
      });
      setAvisoClientes(null);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      setAvisoClientes(null);
      if (idGravado) {
        // Gravou mas não disponibilizou: fica Pendente no grid (ícone Disponibilizar), sem duplicar.
        openNotification({
          message: `Liberação gravada, mas não disponibilizada: ${mensagemDoErro(error)} Ela ficou Pendente na lista.`,
          type: "error",
        });
        if (onSuccess) onSuccess();
        onClose();
      } else {
        mostrarFalhaGravacao(error);
      }
    } finally {
      gravandoRef.current = false;
      setSalvando(false);
    }
  };

  // Plano 22/10/2026 (Frente 1): o aviso já abre com Serial, Nome Fantasia e Razão Social (pedido
  // do PO); sem a Extranet, os nomes ficam vazios e o Sim continua liberado.
  const carregarNomesDoAviso = async (aviso: ClientesAfetados) => {
    const seriais = aviso.clientes.map((c) => c.serial.trim());
    setNomesIndisponiveis(false);
    if (seriais.length === 0) {
      setLinhasAviso([]);
      return;
    }
    try {
      // Extranet lenta não segura o aviso: em 15 s ele abre só com os seriais.
      const { data } = await api.post<NomesClientes>("LiberacaoDiversaPacotes/ClientesAfetados/Nomes", seriais, {
        timeout: 15000,
      });
      const porSerial = new Map(data.clientes.map((c) => [c.serial.trim(), c]));
      setLinhasAviso(seriais.map((serial) => [serial, porSerial.get(serial)?.nomeFantasia ?? "", porSerial.get(serial)?.razaoSocial ?? ""]));
      setNomesIndisponiveis(!data.extranetDisponivel);
    } catch {
      setLinhasAviso(seriais.map((serial) => [serial, "", ""]));
      setNomesIndisponiveis(true);
    }
  };

  const temAlteracoes = () => {
    if (modoLeitura) return false;
    if (!liberacaoId) return tipoLiberacao !== "" || sistema !== "";
    const quemPossuiOriginal = liberacaoCarregadaRef.current?.atualizacaoExistenteCEM?.value ?? null;
    const quemPossuiAtual = exigeQuemPossui && liberarParaQuemPossui ? Number(liberarParaQuemPossui) : null;
    return listasAlteradasRef.current.size > 0 || quemPossuiAtual !== quemPossuiOriginal;
  };

  const handleFechar = () => {
    if (temAlteracoes()) {
      setConfirmarDescarte(true);
      return;
    }
    onClose();
  };

  /** Grava cabeçalho e listas pelo MOF antigo; devolve o id da liberação. Repassa qualquer erro. */
  const gravarLiberacao = async (): Promise<number> => {
    const isLiberacaoCem = ["1", "2", "3", "4", "5", "8"].includes(String(sistema));
    const quemPossuiSelecionado = opcoesQuemPossui.find((o) => o.id === Number(liberarParaQuemPossui));
    const atualizacaoExistenteCEM =
      exigeQuemPossui && liberarParaQuemPossui
        ? { value: Number(liberarParaQuemPossui), label: quemPossuiSelecionado?.label ?? "" }
        : null;
    let idLiberacaoFinal = liberacaoId ?? idCriadoRef.current;

    // Se não tem liberacaoId, precisa criar primeiro (uma vez só, mesmo com nova tentativa)
    if (!idLiberacaoFinal) {
      // Criar liberação diversa básica
      const tipoSelecionado = tiposOpcoes.find((t) => (t.value || t.Value) === tipoLiberacao);
      const sistemaSelecionado = sistemasOpcoes.find((s) => (s.value || s.Value) === sistema);
      
      // Determinar liberação padrão (Liberar CEM ou Liberar PrefSuite)
      const liberacaoDefault = isLiberacaoCem ? "LIBERAR_CEM" : "LIBERAR_PREFSUITE";

      const payload = {
        id: 0,
        disponivel: false,
        // O "quem possui" vai no cabeçalho (IdAtualizacaoCEMExistente) — é o que o Disponibilizar lê
        atualizacaoExistenteCEM: isLiberacaoCem ? atualizacaoExistenteCEM : undefined,
        atualizacaoExistentePrefSuite: !isLiberacaoCem ? null : undefined,
        tipo: tipoSelecionado
          ? { value: tipoSelecionado.value || tipoSelecionado.Value, label: tipoSelecionado.label || tipoSelecionado.Label }
          : undefined,
        liberacao: { value: liberacaoDefault, label: isLiberacaoCem ? "Liberar CEM" : "Liberar PrefSuite" },
        sistema: sistemaSelecionado
          ? { value: Number(sistemaSelecionado.value || sistemaSelecionado.Value), label: sistemaSelecionado.label || sistemaSelecionado.Label }
          : undefined,
      };

      const { data } = await apiAntigo.post("/LiberacaoDiversa", payload);
      idLiberacaoFinal = data.id || data.Id || data.idLiberacaoDiversa || data.IdLiberacaoDiversa;
      idCriadoRef.current = idLiberacaoFinal ?? null;
    } else if (isLiberacaoCem && liberacaoCarregadaRef.current && !liberacaoCarregadaRef.current.disponivel) {
      // Edição: trocou o "quem possui" de uma liberação ainda não disponibilizada → regrava o cabeçalho
      // com os demais campos como vieram (o legado não altera Disponivel nem DataCriacao no update)
      const original = liberacaoCarregadaRef.current;
      const idOriginal = original.atualizacaoExistenteCEM?.value ?? null;
      if ((atualizacaoExistenteCEM?.value ?? null) !== idOriginal) {
        await apiAntigo.post("/LiberacaoDiversa", { ...original, id: liberacaoId, atualizacaoExistenteCEM });
        liberacaoCarregadaRef.current = { ...original, atualizacaoExistenteCEM };
      }
    }

    if (!idLiberacaoFinal) throw new Error("Não foi possível gravar a liberação.");

    // Seleções: na criação, as listas preenchidas; na edição, só as alteradas nesta abertura
    // (inclusive esvaziadas — sem o salvamento automático, é aqui que o desmarcado sai).
    const gravar = (lista: ListaEditavel, itens: DualListItem[]) =>
      liberacaoId ? listasAlteradasRef.current.has(lista) : itens.length > 0;
    if (tipoLiberacao === 1 && gravar("pacotes", pacotesSelecionados)) {
      await salvarPacotes(pacotesSelecionados, idLiberacaoFinal);
    }
    if (tipoLiberacao === 2 && gravar("aplicabilidades", aplicabilidadesSelecionadas)) {
      await salvarAplicabilidades(aplicabilidadesSelecionadas, idLiberacaoFinal);
    }
    if (tipoLiberacao === 3 && gravar("tabelas", tabelasSelecionadas)) {
      await salvarTabelas(tabelasSelecionadas, idLiberacaoFinal);
    }
    if (tipoLiberacao === 4) {
      if (gravar("tipologias", tipologiasSelecionadas)) {
        await salvarTipologias(tipologiasSelecionadas, idLiberacaoFinal);
      }
      if (gravar("clientes", clientesSelecionados)) {
        await salvarClientes(clientesSelecionados, idLiberacaoFinal);
      }
    }
    listasAlteradasRef.current = new Set();
    return idLiberacaoFinal;
  };

  const filtrarLista = (lista: DualListItem[], busca: string) => {
    if (!busca.trim()) return lista;
    const termo = busca.toLowerCase();
    return lista.filter(
      (item) =>
        item.label.toLowerCase().includes(termo) ||
        (item.subtitle && item.subtitle.toLowerCase().includes(termo))
    );
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleFechar} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: "800px",
          }
        }}
      >
        <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600 }}>
          {modoLeitura ? "Consultar liberação diversa (já disponibilizada)" : "Adicionar liberações diversas"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Card superior com campos principais */}
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 3,
                mb: 3,
                bgcolor: "background.paper",
              }}
            >
              <Grid container spacing={2}>
                 <Grid item xs={12} md={6}>
                   <TextField
                     select
                     fullWidth
                     label="Tipo liberação *"
                     value={tipoLiberacao}
                     onChange={(e) => setTipoLiberacao(e.target.value as number | "")}
                     disabled={modoLeitura}
                     helperText="Obrigatório"
                     FormHelperTextProps={{
                       sx: { color: "#3C90AD" }
                     }}
                   >
                     {tiposOpcoes.map((opt) => (
                       <MenuItem key={opt.value} value={opt.value}>
                         {opt.label}
                       </MenuItem>
                     ))}
                   </TextField>
                 </Grid>
                 <Grid item xs={12} md={6}>
                   <TextField
                     select
                     fullWidth
                     label="Sistema *"
                     value={sistema}
                     onChange={(e) => setSistema(e.target.value)}
                     disabled={modoLeitura}
                     helperText="Obrigatório"
                     FormHelperTextProps={{
                       sx: { color: "#3C90AD" }
                     }}
                   >
                     {sistemasOpcoes.map((opt: any) => (
                       <MenuItem key={opt.value || opt.Value} value={opt.value || opt.Value}>
                         {opt.label || opt.Label}
                       </MenuItem>
                     ))}
                   </TextField>
                 </Grid>
              </Grid>

               {/* Radio Buttons Cadastro nível 1 / nível 2 - Aparece para TODOS os tipos */}
               {tipoLiberacao && (
                 <Box sx={{ mt: 3 }}>
                   <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
                     Realizar liberação para quem possui a liberação
                   </Typography>
                   <RadioGroup
                     row
                     value={nivelCadastro}
                     onChange={(e) => {
                       // O nível só troca a lista do "quem possui"; o conteúdo marcado continua.
                       // No tipo 4 o layout muda com o nível, então a seleção recomeça.
                       setNivelCadastro(e.target.value as "nivel1" | "nivel2");
                       setLiberarParaQuemPossui("");
                       if (tipoLiberacao === 4) {
                         setTipologiasSelecionadas([]);
                         setClientesSelecionados([]);
                         marcarAlterada("tipologias");
                         marcarAlterada("clientes");
                       }
                     }}
                     sx={{ gap: 3 }}
                   >
                     <FormControlLabel
                       value="nivel1"
                       control={<Radio />}
                       label="Cadastro nível 1"
                       disabled={modoLeitura}
                     />
                     <FormControlLabel
                       value="nivel2"
                       control={<Radio />}
                       label="Cadastro nível 2"
                       disabled={modoLeitura}
                     />
                   </RadioGroup>
                   
                   {/* Dropdown "Liberar para quem possui": nível 1 = pacotes N1; nível 2 = Projetista + Linha
                       liberados ao menos para um serial (P2 de 09/10/2026). No tipo 4 com nível 2 o
                       "quem possui" é o Projetista/Linha da própria seção. */}
                   {exigeQuemPossui && (
                     <Box sx={{ mt: 2 }}>
                       <TextField
                         select
                         fullWidth
                         label={
                           nivelCadastro === "nivel2"
                             ? "Liberar para quem possui (Projetista + Linha) *"
                             : "Liberar para quem possui *"
                         }
                         value={liberarParaQuemPossui}
                         onChange={(e) => setLiberarParaQuemPossui(e.target.value as number | "")}
                         disabled={modoLeitura}
                         helperText={modoLeitura ? "Liberação já disponibilizada" : carregandoQuemPossui ? "Carregando..." : "Obrigatório"}
                         size="small"
                         FormHelperTextProps={{
                           sx: { color: "#3C90AD" }
                         }}
                       >
                         <MenuItem value="">{carregandoQuemPossui ? "Carregando..." : "Selecione..."}</MenuItem>
                         {opcoesQuemPossui.map((pacote) => (
                           <MenuItem key={pacote.id} value={pacote.id}>
                             {pacote.label}
                           </MenuItem>
                         ))}
                       </TextField>
                     </Box>
                   )}
                 </Box>
               )}
            </Box>

            {/* Card com conteúdo dinâmico por tipo */}
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
                p: 3,
              }}
            >
               {/* TIPO 1: Liberar cadastros nível 1 */}
               {tipoLiberacao === 1 && (
                 <>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Tipo + Referências + Linha
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Busque a referência ou linha"
                    value={buscaN1}
                    onChange={(e) => setBuscaN1(e.target.value)}
                    size="small"
                    sx={{ 
                      mb: 2,
                      borderRadius: 1.5,
                      bgcolor: "rgba(248, 250, 255, 1)",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "rgba(226, 232, 240, 0.8)",
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon fontSize="small" sx={{ color: "rgba(148, 163, 184, 1)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box
                    sx={{
                      maxHeight: 400,
                      overflow: "auto",
                    }}
                  >
                    {filtrarLista(pacotesDisponiveis, buscaN1).map((item) => {
                      const isChecked = pacotesSelecionados.some((p) => p.id === item.id);
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <FormControlLabel
                            sx={{ 
                              width: "100%", 
                              m: 0, 
                              py: 0.5, 
                              px: 1,
                              display: "flex",
                              justifyContent: "space-between",
                              "& .MuiFormControlLabel-label": {
                                flex: 1,
                              },
                            }}
                            control={
                              <Checkbox
                                disabled={modoLeitura}
                                checked={isChecked}
                                onChange={(e) => {
                                  let newSelection: DualListItem[];
                                  if (e.target.checked) {
                                    newSelection = [...pacotesSelecionados, item];
                                  } else {
                                    newSelection = pacotesSelecionados.filter((p) => p.id !== item.id);
                                  }
                                  setPacotesSelecionados(newSelection);
                                  if (liberacaoId) {
                                    marcarAlterada("pacotes");
                                  }
                                }}
                                size="small"
                                sx={{
                                  color: "#75B0C7",
                                  "&.Mui-checked": {
                                    color: "#3C90AD",
                                  },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ color: "#3C90AD" }}>
                                  {item.label}
                                </Typography>
                                {item.subtitle && (
                                  <Typography variant="caption" sx={{ color: "#75B0C7" }}>
                                    {item.subtitle}
                                  </Typography>
                                )}
                              </Box>
                            }
                            labelPlacement="start"
                          />
                        </Box>
                      );
                    })}
                    {filtrarLista(pacotesDisponiveis, buscaN1).length === 0 && (
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum item encontrado
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {/* TIPO 2: Aplicabilidades */}
              {tipoLiberacao === 2 && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Aplicabilidades
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Busque a aplicabilidade"
                    value={buscaAplic}
                    onChange={(e) => setBuscaAplic(e.target.value)}
                    size="small"
                    sx={{ 
                      mb: 2,
                      borderRadius: 1.5,
                      bgcolor: "rgba(248, 250, 255, 1)",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "rgba(226, 232, 240, 0.8)",
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon fontSize="small" sx={{ color: "rgba(148, 163, 184, 1)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box
                    sx={{
                      maxHeight: 400,
                      overflow: "auto",
                    }}
                  >
                    {filtrarLista(aplicabilidadesDisponiveis, buscaAplic).map((item) => {
                      const isChecked = aplicabilidadesSelecionadas.some((a) => a.id === item.id);
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <FormControlLabel
                            sx={{ 
                              width: "100%", 
                              m: 0, 
                              py: 0.5, 
                              px: 1,
                              display: "flex",
                              justifyContent: "space-between",
                              "& .MuiFormControlLabel-label": {
                                flex: 1,
                              },
                            }}
                            control={
                              <Checkbox
                                disabled={modoLeitura}
                                checked={isChecked}
                                onChange={(e) => {
                                  let newSelection: DualListItem[];
                                  if (e.target.checked) {
                                    newSelection = [...aplicabilidadesSelecionadas, item];
                                  } else {
                                    newSelection = aplicabilidadesSelecionadas.filter(
                                      (a) => a.id !== item.id
                                    );
                                  }
                                  setAplicabilidadesSelecionadas(newSelection);
                                  if (liberacaoId) {
                                    marcarAlterada("aplicabilidades");
                                  }
                                }}
                                size="small"
                                sx={{
                                  color: "#75B0C7",
                                  "&.Mui-checked": {
                                    color: "#3C90AD",
                                  },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ color: "#3C90AD" }}>
                                  {item.label}
                                </Typography>
                              </Box>
                            }
                            labelPlacement="start"
                          />
                        </Box>
                      );
                    })}
                    {filtrarLista(aplicabilidadesDisponiveis, buscaAplic).length === 0 && (
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum item encontrado
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {/* TIPO 3: Tabela de preço */}
              {tipoLiberacao === 3 && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Tabelas de preços
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Busque a tabela de preço"
                    value={buscaTabela}
                    onChange={(e) => setBuscaTabela(e.target.value)}
                    size="small"
                    sx={{ 
                      mb: 2,
                      borderRadius: 1.5,
                      bgcolor: "rgba(248, 250, 255, 1)",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "rgba(226, 232, 240, 0.8)",
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon fontSize="small" sx={{ color: "rgba(148, 163, 184, 1)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box
                    sx={{
                      maxHeight: 400,
                      overflow: "auto",
                    }}
                  >
                    {filtrarLista(tabelasDisponiveis, buscaTabela).map((item) => {
                      const isChecked = tabelasSelecionadas.some((t) => t.id === item.id);
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <FormControlLabel
                            sx={{ 
                              width: "100%", 
                              m: 0, 
                              py: 0.5, 
                              px: 1,
                              display: "flex",
                              justifyContent: "space-between",
                              "& .MuiFormControlLabel-label": {
                                flex: 1,
                              },
                            }}
                            control={
                              <Checkbox
                                disabled={modoLeitura}
                                checked={isChecked}
                                onChange={(e) => {
                                  let newSelection: DualListItem[];
                                  if (e.target.checked) {
                                    newSelection = [...tabelasSelecionadas, item];
                                  } else {
                                    newSelection = tabelasSelecionadas.filter((t) => t.id !== item.id);
                                  }
                                  setTabelasSelecionadas(newSelection);
                                  if (liberacaoId) {
                                    marcarAlterada("tabelas");
                                  }
                                }}
                                size="small"
                                sx={{
                                  color: "#75B0C7",
                                  "&.Mui-checked": {
                                    color: "#3C90AD",
                                  },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ color: "#3C90AD" }}>
                                  {item.label}
                                </Typography>
                              </Box>
                            }
                            labelPlacement="start"
                          />
                        </Box>
                      );
                    })}
                    {filtrarLista(tabelasDisponiveis, buscaTabela).length === 0 && (
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum item encontrado
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {/* TIPO 4: Liberar cadastros nível 2 - N1 */}
              {tipoLiberacao === 4 && nivelCadastro === "nivel1" && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Projetistas + Linha/Sistema
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Busque a referência ou linha"
                    value={buscaN2}
                    onChange={(e) => setBuscaN2(e.target.value)}
                    size="small"
                    sx={{ 
                      mb: 2,
                      borderRadius: 1.5,
                      bgcolor: "rgba(248, 250, 255, 1)",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "rgba(226, 232, 240, 0.8)",
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon fontSize="small" sx={{ color: "rgba(148, 163, 184, 1)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box
                    sx={{
                      maxHeight: 400,
                      overflow: "auto",
                    }}
                  >
                    {filtrarLista(tipologiasDisponiveis, buscaN2).map((item) => {
                      const isChecked = tipologiasSelecionadas.some((t) => t.id === item.id);
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <FormControlLabel
                            sx={{ 
                              width: "100%", 
                              m: 0, 
                              py: 0.5, 
                              px: 1,
                              display: "flex",
                              justifyContent: "space-between",
                              "& .MuiFormControlLabel-label": {
                                flex: 1,
                              },
                            }}
                            control={
                              <Checkbox
                                disabled={modoLeitura}
                                checked={isChecked}
                                onChange={(e) => {
                                  let newSelection: DualListItem[];
                                  if (e.target.checked) {
                                    newSelection = [...tipologiasSelecionadas, item];
                                  } else {
                                    newSelection = tipologiasSelecionadas.filter(
                                      (t) => t.id !== item.id
                                    );
                                  }
                                  setTipologiasSelecionadas(newSelection);
                                  if (liberacaoId) {
                                    marcarAlterada("tipologias");
                                  }
                                }}
                                size="small"
                                sx={{
                                  color: "#75B0C7",
                                  "&.Mui-checked": {
                                    color: "#3C90AD",
                                  },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ color: "#3C90AD" }}>
                                  {item.label}
                                </Typography>
                                {item.subtitle && (
                                  <Typography variant="caption" sx={{ color: "#75B0C7" }}>
                                    {item.subtitle}
                                  </Typography>
                                )}
                              </Box>
                            }
                            labelPlacement="start"
                          />
                        </Box>
                      );
                    })}
                    {filtrarLista(tipologiasDisponiveis, buscaN2).length === 0 && (
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum item encontrado
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {/* TIPO 4: Liberar cadastros nível 2 - N2 */}
              {tipoLiberacao === 4 && nivelCadastro === "nivel2" && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Liberar para quem possui
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Projetista"
                        value={projetistaId}
                        onChange={(e) => setProjetistaId(e.target.value as number | "")}
                        size="small"
                      >
                        <MenuItem value="">Todos</MenuItem>
                        {projetistasOpcoes.map((p: any) => (
                          <MenuItem key={p.value || p.Value} value={p.value || p.Value}>
                            {p.label || p.Label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Linha"
                        value={linhaId}
                        onChange={(e) => setLinhaId(e.target.value as number | "")}
                         size="small"
                       >
                         <MenuItem value="">Todas</MenuItem>
                         {linhasN2Opcoes.map((l: any) => (
                           <MenuItem key={l.value || l.Value} value={l.value || l.Value}>
                             {l.label || l.Label}
                           </MenuItem>
                         ))}
                       </TextField>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Tipo + Referências + Linha
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Busque a referência ou linha"
                    value={buscaN2}
                    onChange={(e) => setBuscaN2(e.target.value)}
                    size="small"
                    sx={{ 
                      mb: 2,
                      borderRadius: 1.5,
                      bgcolor: "rgba(248, 250, 255, 1)",
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "rgba(226, 232, 240, 0.8)",
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon fontSize="small" sx={{ color: "rgba(148, 163, 184, 1)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box
                    sx={{
                      maxHeight: 400,
                      overflow: "auto",
                    }}
                  >
                    {filtrarLista(tipologiasDisponiveis, buscaN2).map((item) => {
                      const isChecked = tipologiasSelecionadas.some((t) => t.id === item.id);
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            "&:hover": { bgcolor: "action.hover" },
                          }}
                        >
                          <FormControlLabel
                            sx={{ 
                              width: "100%", 
                              m: 0, 
                              py: 0.5, 
                              px: 1,
                              display: "flex",
                              justifyContent: "space-between",
                              "& .MuiFormControlLabel-label": {
                                flex: 1,
                              },
                            }}
                            control={
                              <Checkbox
                                disabled={modoLeitura}
                                checked={isChecked}
                                onChange={(e) => {
                                  let newSelection: DualListItem[];
                                  if (e.target.checked) {
                                    newSelection = [...tipologiasSelecionadas, item];
                                  } else {
                                    newSelection = tipologiasSelecionadas.filter(
                                      (t) => t.id !== item.id
                                    );
                                  }
                                  setTipologiasSelecionadas(newSelection);
                                  if (liberacaoId) {
                                    marcarAlterada("tipologias");
                                  }
                                }}
                                size="small"
                                sx={{
                                  color: "#75B0C7",
                                  "&.Mui-checked": {
                                    color: "#3C90AD",
                                  },
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ color: "#3C90AD" }}>
                                  {item.label}
                                </Typography>
                                {item.subtitle && (
                                  <Typography variant="caption" sx={{ color: "#75B0C7" }}>
                                    {item.subtitle}
                                  </Typography>
                                )}
                              </Box>
                            }
                            labelPlacement="start"
                          />
                        </Box>
                      );
                    })}
                    {filtrarLista(tipologiasDisponiveis, buscaN2).length === 0 && (
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Nenhum item encontrado
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {nivelCadastro === "nivel2" && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Clientes
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Buscar cliente"
                        value={buscaCliente}
                        onChange={(e) => setBuscaCliente(e.target.value)}
                        size="small"
                        sx={{ 
                          mb: 2,
                          borderRadius: 1.5,
                          bgcolor: "rgba(248, 250, 255, 1)",
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "rgba(226, 232, 240, 0.8)",
                            },
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <SearchIcon fontSize="small" sx={{ color: "rgba(148, 163, 184, 1)" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Box
                        sx={{
                          maxHeight: 400,
                          overflow: "auto",
                        }}
                      >
                        {filtrarLista(clientesDisponiveis, buscaCliente).map((item) => {
                          const isChecked = clientesSelecionados.some((c) => c.id === item.id);
                          return (
                            <Box
                              key={item.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <FormControlLabel
                                sx={{ 
                                  width: "100%", 
                                  m: 0, 
                                  py: 0.5, 
                                  px: 1,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  "& .MuiFormControlLabel-label": {
                                    flex: 1,
                                  },
                                }}
                                control={
                                  <Checkbox
                                    disabled={modoLeitura}
                                    checked={isChecked}
                                    onChange={(e) => {
                                      let newSelection: DualListItem[];
                                      if (e.target.checked) {
                                        newSelection = [...clientesSelecionados, item];
                                      } else {
                                        newSelection = clientesSelecionados.filter(
                                          (c) => c.id !== item.id
                                        );
                                      }
                                      setClientesSelecionados(newSelection);
                                      if (liberacaoId) {
                                        marcarAlterada("clientes");
                                      }
                                    }}
                                    size="small"
                                    sx={{
                                      color: "#75B0C7",
                                      "&.Mui-checked": {
                                        color: "#3C90AD",
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography variant="body2" sx={{ color: "#3C90AD" }}>
                                      {item.label}
                                    </Typography>
                                  </Box>
                                }
                                labelPlacement="start"
                              />
                            </Box>
                          );
                        })}
                        {filtrarLista(clientesDisponiveis, buscaCliente).length === 0 && (
                          <Box sx={{ p: 3, textAlign: "center" }}>
                            <Typography variant="body2" color="text.secondary">
                              Nenhum item encontrado
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFechar}>{modoLeitura ? "FECHAR" : "CANCELAR"}</Button>
          {!modoLeitura && (
            <Button variant="contained" onClick={handleSalvar} disabled={isLoading || salvando}>
              SALVAR
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* Plano 21/10/2026 (Frente 8): aviso do Salvar — texto pedido pelo PO em 20/10. */}
      <Dialog open={!!avisoClientes} onClose={() => setAvisoClientes(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600 }}>Confirmar liberação</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            {avisoClientes?.totalSeriais ?? 0} clientes serão afetados por essa liberação, tem certeza de que deseja continuar?
          </Typography>
          {(avisoClientes?.totalSeriais ?? 0) === 0 && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              Nenhum cliente seria atingido: revise o "Liberar para quem possui" ou os clientes escolhidos.
            </Typography>
          )}
          {nomesIndisponiveis && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
              Extranet indisponível: nomes não carregados.
            </Typography>
          )}
          <ListaCopiarBaixar
            nomeArquivo="clientes-afetados-liberacao"
            colunas={[{ titulo: "Serial" }, { titulo: "Nome Fantasia" }, { titulo: "Razão Social" }]}
            linhas={linhasAviso}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvisoClientes(null)}>NÃO</Button>
          <Button
            variant="contained"
            onClick={handleConfirmarAvisoClientes}
            disabled={salvando || (avisoClientes?.totalSeriais ?? 0) === 0}
          >
            SIM
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmarDescarte} onClose={() => setConfirmarDescarte(false)}>
        <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600 }}>Descartar alterações?</DialogTitle>
        <DialogContent>
          <Typography>As alterações feitas nesta liberação ainda não foram salvas e serão perdidas.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarDescarte(false)}>CONTINUAR EDITANDO</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmarDescarte(false);
              onClose();
            }}
          >
            DESCARTAR
          </Button>
        </DialogActions>
      </Dialog>
      <BackdropLoading isOpen={isLoading && !salvando} />
      <BackdropLoading isOpen={salvando} acimaDosDialogos mensagem={mensagemAndamento} />
    </>
  );
};

export default ModalAdicionarLiberacaoDiversa;

